import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useShortcut } from '@/hooks/useShortcut';
import { useTheme } from '@/hooks/useTheme';
import {
  COMMAND_PALETTE,
  CREATE_NEW,
  GO_COMPLIANCE,
  GO_DASHBOARD,
  GO_ENTITIES,
} from '@/lib/shortcuts';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';

export function useCommandPalette() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ui.commandPaletteOpen);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { toggle: toggleTheme } = useTheme();

  const setIsOpen = useCallback((open: boolean) => {
    dispatch(uiActions.setCommandPaletteOpen(open));
  }, [dispatch]);

  const open = useCallback(() => setIsOpen(true), [setIsOpen]);
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);
  const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen, setIsOpen]);

  useShortcut({ ...COMMAND_PALETTE, onTrigger: toggle });
  useShortcut({ ...GO_DASHBOARD, onTrigger: () => navigate('/dashboard') });
  useShortcut({ ...GO_ENTITIES, onTrigger: () => navigate('/entities') });
  useShortcut({ ...GO_COMPLIANCE, onTrigger: () => navigate('/compliance') });
  useShortcut({ ...CREATE_NEW, onTrigger: () => navigate('/entities') });

  const runAndClose = useCallback((action: () => void) => {
    action();
    close();
  }, [close]);

  return {
    isOpen,
    open,
    close,
    toggle,
    setIsOpen,
    navigate: (path: string) => runAndClose(() => navigate(path)),
    search: (query: string) => {
      const q = query.trim().slice(0, 128);
      if (!q) return;
      runAndClose(() => navigate(`/search?q=${encodeURIComponent(q)}`));
    },
    searchAssociations: (query: string) => {
      const q = query.trim().slice(0, 128);
      if (!q) return;
      runAndClose(() => navigate(`/search?q=${encodeURIComponent(q)}&type=association`));
    },
    logout: () => runAndClose(() => { void logout(); }),
    toggleTheme: () => runAndClose(toggleTheme),
  };
}

export type CommandPaletteController = ReturnType<typeof useCommandPalette>;
